import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { toast } from 'vue-sonner';
import http from '@/services/http';
import type { SavedViewRecord, SharedSavedViewRecord } from '@/utils/savedViews';
import type { CursorMeta } from '@/types/types';
import { savedViewsEntityKey } from '@/composables/api/useSavedViewsApi';

/**
 * API layer for the shared-view library and share links (SAU-310).
 *
 * Three endpoints, and they answer the SAME permission question even though
 * they look different — the library listing, the share-key resolver and import
 * are all filtered by model permission on the server. A view for an entity you
 * cannot see is absent from the list, resolves as "not found", and cannot be
 * imported. Nothing here needs to enforce that; it just has to not paper over
 * the results.
 *
 * Choreography matches useSavedViewsApi: entity travels in the mutation
 * variables so a mid-flight page switch cannot settle against the wrong key.
 */

const RESOURCE_BASE = '/api/shared_saved_view';

/**
 * One page of one entity's library under one search term.
 *
 * The cursor is part of the key, so stepping back to a page you have already seen
 * is served from cache — Prev is instant and does not re-hit MySQL — and so is
 * clearing the search box.
 */
export const sharedSavedViewsQueryKey = (
  entityId: string,
  search: string,
  cursor: string | null,
) => ['sharedSavedViews', entityId, search, cursor] as const;

/** Every cached page and term for one entity — what a mutation invalidates. */
export const sharedSavedViewsEntityKey = (entityId: string) =>
  ['sharedSavedViews', entityId] as const;

/** Rows per page. The server clamps anything larger; this just has to agree. */
const PAGE_SIZE = 50;

/** One page of the library, with the cursor meta the pager navigates on. */
export interface SharedSavedViewPage {
  rows: SharedSavedViewRecord[];
  meta: CursorMeta | null;
}

export interface SharedSavedViewPublishInput {
  entity: string;
  savedViewId: number;
}

export interface SharedSavedViewDeleteInput {
  entity: string;
  search: string;
  /** The page that was on screen — the one whose cache the patch must land on. */
  cursor: string | null;
  sharedSavedViewId: number;
}

export interface SharedSavedViewImportInput {
  entity: string;
  shareKey: string;
  /** Optional rename on the way in; the server suffixes a collision either way. */
  name?: string;
}

interface ListEnvelope<T> {
  data: T[];
  meta?: CursorMeta;
}

interface ItemEnvelope<T> {
  data: T;
}

/**
 * The library for one page's entity, optionally narrowed by a search over the
 * view name and the sharer's name.
 *
 * `search` is part of the query key rather than a refetch trigger, so typing
 * back to a previous term is served from cache instead of re-hitting MySQL.
 */
export function useSharedSavedViewsQuery(
  entityId: MaybeRefOrGetter<string>,
  search: MaybeRefOrGetter<string>,
  cursor: MaybeRefOrGetter<string | null>,
  enabled: MaybeRefOrGetter<boolean>,
) {
  return useQuery({
    queryKey: computed(() =>
      sharedSavedViewsQueryKey(toValue(entityId), toValue(search), toValue(cursor)),
    ),
    queryFn: async ({
      queryKey: [, entity, term, pageCursor],
    }): Promise<SharedSavedViewPage> => {
      const response = await http.get<ListEnvelope<SharedSavedViewRecord>>(RESOURCE_BASE, {
        params: {
          entity,
          perPage: PAGE_SIZE,
          ...(term ? { search: term } : {}),
          ...(pageCursor ? { cursor: pageCursor } : {}),
        },
      });
      // NOT `?? []` — see the listing in useSavedViewsApi for why. axios hands
      // back a raw string when a 200 is not parseable JSON, and defaulting to an
      // empty array renders that as "nobody has shared a view for this page yet".
      const rows = response.data?.data;
      if (!Array.isArray(rows)) {
        throw new Error('The shared-views listing did not answer with a data array.');
      }

      return { rows, meta: response.data?.meta ?? null };
    },
    // Only fetched while the Browse dialog is open — the library is not needed
    // to render the toolbar, and this is the one query here that hits a LIKE.
    enabled: computed(() => toValue(enabled)),
    // Shorter than the saved-views list on purpose: other people publish into
    // this, so it genuinely does go stale without this client doing anything.
    staleTime: 30_000,
    retry: false,
    // The previous term's rows stay on screen while the next term loads, so
    // typing does not blink the list to a spinner on every debounce tick.
    placeholderData: keepPreviousData,
  });
}

/**
 * Resolve a `?s=<shareKey>` link. Not a mutation — it reads one row — but it is
 * a one-shot lookup rather than a list, so it is keyed by the share key.
 *
 * A 404 here means EITHER no such key OR an entity this user cannot view, and
 * the two are deliberately indistinguishable: telling them apart would confirm
 * the row exists, which is the leak the permission filter exists to close. The
 * import page therefore says "invalid, or you don't have access" rather than
 * guessing which.
 */
export function useSharedSavedViewByKeyQuery(shareKey: MaybeRefOrGetter<string | null>) {
  return useQuery({
    queryKey: computed(() => ['sharedSavedViewByKey', toValue(shareKey)] as const),
    queryFn: async ({ queryKey: [, key] }): Promise<SharedSavedViewRecord | null> => {
      const response = await http.get<ItemEnvelope<SharedSavedViewRecord>>(
        `${RESOURCE_BASE}/key/${encodeURIComponent(key as string)}`,
      );
      return response.data?.data ?? null;
    },
    enabled: computed(() => Boolean(toValue(shareKey))),
    staleTime: Infinity,
    retry: false,
  });
}

/** Publish one of your own saved views to the library, returning its share link row. */
export function usePublishSharedSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SharedSavedViewPublishInput): Promise<SharedSavedViewRecord> => {
      const response = await http.post<ItemEnvelope<SharedSavedViewRecord>>(RESOURCE_BASE, {
        saved_view_id: input.savedViewId,
      });
      const published = response.data?.data;
      if (!published) {
        throw new Error('Share response is missing its data envelope.');
      }
      return published;
    },
    // No optimistic patch: the share KEY is generated server-side and the whole
    // point of the call is to learn it, so there is nothing truthful to show
    // early. The user is told it failed rather than told it succeeded.
    onError: () => {
      toast.error('Could not share the view.');
    },
    onSettled: (_data, _error, input) => {
      // Any open library list for this entity now has one more row in it.
      queryClient.invalidateQueries({ queryKey: sharedSavedViewsEntityKey(input.entity) });
    },
  });
}

/** Remove a library entry you published. */
export function useDeleteSharedSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sharedSavedViewId }: SharedSavedViewDeleteInput) => {
      await http.delete(`${RESOURCE_BASE}/${sharedSavedViewId}`);
      return sharedSavedViewId;
    },
    onMutate: async (input) => {
      const queryKey = sharedSavedViewsQueryKey(input.entity, input.search, input.cursor);
      const current = queryClient.getQueryData<SharedSavedViewPage>(queryKey);
      if (current === undefined) {
        // Nothing to patch yet — this page's first fetch is still in flight (the
        // rows on screen are the previous page's placeholder). Writing an empty
        // page into an empty key blanked the whole list to "No shared views
        // match" until the settle-time refetch. The invalidation below is enough.
        return { queryKey, removed: undefined };
      }
      await queryClient.cancelQueries({ queryKey });
      const removed = current.rows.find((shared) => shared.id === input.sharedSavedViewId);
      queryClient.setQueryData<SharedSavedViewPage>(queryKey, (old) =>
        old
          ? { ...old, rows: old.rows.filter((shared) => shared.id !== input.sharedSavedViewId) }
          : old,
      );
      return { queryKey, removed };
    },
    onError: (_error, _input, context) => {
      const removed = context?.removed;
      if (context && removed) {
        queryClient.setQueryData<SharedSavedViewPage>(context.queryKey, (old) => {
          if (!old) {
            return old;
          }
          return old.rows.some((shared) => shared.id === removed.id)
            ? old
            : { ...old, rows: [...old.rows, removed] };
        });
      }
      toast.error('Could not remove the shared view.');
    },
    onSettled: (_data, _error, input) => {
      queryClient.invalidateQueries({ queryKey: sharedSavedViewsEntityKey(input.entity) });
    },
  });
}

/**
 * Fork a published view into your own saved views.
 *
 * Everything is a fork: this creates an INDEPENDENT copy with its own id, and
 * nothing tracks anything afterwards. The server suffixes the name if the
 * recipient already uses it, and refuses outright — as a 404, matching the
 * resolver — if the entity is one they cannot view.
 */
export function useImportSharedSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SharedSavedViewImportInput): Promise<SavedViewRecord> => {
      const response = await http.post<ItemEnvelope<SavedViewRecord>>(`${RESOURCE_BASE}/import`, {
        share_key: input.shareKey,
        ...(input.name ? { name: input.name } : {}),
      });
      const imported = response.data?.data;
      if (!imported) {
        throw new Error('Import response is missing its data envelope.');
      }
      return imported;
    },
    // No optimistic patch: the server decides the final name (it may suffix),
    // so an optimistic row would show a name the user never ends up with.
    onError: () => {
      toast.error('Could not import the view.');
    },
    onSettled: (_data, _error, input) => {
      // The recipient's own list for that entity gained a row — under every
      // search term they have cached, not just whichever one is on screen.
      queryClient.invalidateQueries({ queryKey: savedViewsEntityKey(input.entity) });
    },
  });
}
